import React, { useState, useEffect } from 'react';
import apiService from '../api/apiService';
import './TablePages.css';

const CMS_OPTIONS = [
  { key: 'cms_terms', label: 'Terms & Conditions' },
  { key: 'cms_privacy', label: 'Privacy Policy' },
  { key: 'cms_about', label: 'About' },
  { key: 'cms_help', label: 'Help & Support' },
] as const;

const CMS: React.FC = () => {
  const [selectedKey, setSelectedKey] = useState<string>(CMS_OPTIONS[0].key);
  const [content, setContent] = useState('');
  const [allValues, setAllValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data } = await apiService.get('/admin/settings');
      const values: Record<string, string> = {};
      CMS_OPTIONS.forEach(({ key }) => {
        const row = data.find((s: { key: string }) => s.key === key);
        values[key] = row?.value ?? '';
      });
      setAllValues(values);
      setContent(values[selectedKey] ?? '');
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (Object.keys(allValues).length > 0) {
      setContent(allValues[selectedKey] ?? '');
    }
  }, [selectedKey, allValues]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      await apiService.put(`/admin/settings/${selectedKey}`, { value: content });
      setAllValues((prev) => ({ ...prev, [selectedKey]: content }));
      setMessage('Saved successfully.');
    } catch (err: any) {
      setMessage(err.response?.data?.detail || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const selectedLabel = CMS_OPTIONS.find((o) => o.key === selectedKey)?.label ?? selectedKey;

  if (loading) return <p>Loading...</p>;

  return (
    <div className="table-page">
      <div className="section-card">
        <h2>CMS Content</h2>
        <p className="muted">Content shown in User and Distributor apps. Select a section below to edit.</p>

        <label style={{ fontWeight: 600, display: 'block', marginTop: '16px' }}>Section</label>
        <select
          value={selectedKey}
          onChange={(e) => setSelectedKey(e.target.value)}
          className="input-field"
          style={{ width: '100%', maxWidth: '320px', marginTop: '6px', padding: '10px 12px' }}
        >
          {CMS_OPTIONS.map(({ key, label }) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>

        <form onSubmit={handleSave} style={{ marginTop: '20px' }}>
          <label style={{ fontWeight: 600 }}>{selectedLabel}</label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="input-field"
            rows={12}
            style={{ width: '100%', marginTop: '8px', fontFamily: 'inherit' }}
            placeholder={`Enter ${selectedLabel} content...`}
          />
          <button type="submit" disabled={saving} className="btn-primary" style={{ marginTop: '12px' }}>
            {saving ? 'Saving...' : `Save ${selectedLabel}`}
          </button>
        </form>

        {message && <p className="message" style={{ marginTop: '12px' }}>{message}</p>}
      </div>
    </div>
  );
};

export default CMS;
